// =============================================================================
// io/sync/goalTrades.ts — the trades the Goals Progress card measures. #48
// -----------------------------------------------------------------------------
// ── WHY ANOTHER TRADES READ ──
//
// Three of the four goal templates need fields no existing read carries, and
// the card was rendering "not tracked here yet" for all three because of it:
//
//   loadTradeMetrics    11 columns. No `annotated`, no `booleans`, no
//                       `mistake_tags` — so `boolean-adherence` and `tag-cap`
//                       cannot run on it at all. This is what Goals used.
//   loadTradesInRange   the trades TABLE's columns, and it exists to draw a
//                       table. Scoped to the global date filter, which Goals
//                       must not ride — see below.
//   loadLeakUniverse    pulls `extras` (the fills), the widest read per row in
//                       the client. Goals needs none of it.
//
// So this is its own read, justified exactly as `leakUniverse.ts` and
// `coaching.ts` are: every column earns its place because a goal stops
// measuring without it.
//
// ── 🚨 IT RIDES THE GOALS' OWN WINDOW, NOT THE GLOBAL FILTER ──
//
// A goal says "this month". It has to keep meaning this month when the trader
// filters the Trades tab to last March — the same rule the Coach's Report has.
// The caller bounds this by `earliestGoalStart`, padded for the session
// boundary, and issues NO read at all when there are no goals.
//
// ── ⚠ THE COST, STATED RATHER THAN DISCOVERED ──
//
// Seven small columns plus one jsonb (`booleans`, six flags) and one text array
// (`mistake_tags`). Measured on the real book when `TABLE_KEYS` took the same
// two: across 709 trades, `grade` + `notes` + `mistakeTags` totalled 14.9 KB,
// of which mistake tags were 1.9 KB. `booleans` is smaller again — six short
// keys, most trades carrying none. This read is bounded to the goal window.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { PAGE_ROWS } from './load';

/**
 * The columns, and what stops working without each.
 *
 *   account_id                   a goal scoped to one account
 *   date, session_date           the window, through `effectiveTradeDate`
 *   net_pnl                      `dollar-pl`
 *   annotated                    ⚠ `boolean-adherence` and `tag-cap` BOTH open
 *                                with `annotated !== false`. Without it an
 *                                untouched import counts as a judged trade and
 *                                drags every adherence percentage toward zero.
 *   booleans                     `boolean-adherence` over trades
 *   mistake_tags                 `tag-cap`
 */
export const GOAL_TRADE_COLUMNS: readonly string[] = [
  'account_id', 'date', 'session_date', 'net_pnl',
  'annotated', 'booleans', 'mistake_tags',
];

export const GOAL_TRADE_SELECT: string = GOAL_TRADE_COLUMNS.join(',');

/** Exactly the shape `domain/goals` reads, and nothing more. */
export interface GoalTradeRow {
  accountId: string | null;
  date: string | null;
  sessionDate: string | null;
  netPnl: number | null;
  annotated: boolean | null;
  booleans: Record<string, unknown>;
  mistakeTags: readonly string[];
}

/**
 * Every trade since `since`, oldest first.
 *
 * ⚠ THROWS on a failed page rather than returning what it has. The caller is
 * `useGoals`, which funnels the error into the activity log and renders
 * "couldn't load your goals" — and a PARTIAL trade set here is the worst
 * outcome available: every goal renders a confident percentage over a
 * population nobody asked for, and nothing on screen looks wrong.
 *
 * ⚠ Ordered by `id`, like every other paged read in this client. `range()` over
 * an unordered query has no defined page boundary, so a concurrent insert can
 * shift rows between pages — dropping one and duplicating another.
 */
export async function loadGoalTrades(
  sb: SupabaseClient,
  since: string,
): Promise<GoalTradeRow[]> {
  const out: GoalTradeRow[] = [];

  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('trades')
      .select(GOAL_TRADE_SELECT)
      .gte('date', since)
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    if (error) {
      throw new Error(`loadGoalTrades: page ${from / PAGE_ROWS + 1} failed: ${error.message}`);
    }
    if (!data || !data.length) break;

    for (const r of data as unknown as Record<string, unknown>[]) {
      out.push({
        accountId: (r['account_id'] as string | null) ?? null,
        date: (r['date'] as string | null) ?? null,
        sessionDate: (r['session_date'] as string | null) ?? null,
        netPnl: (r['net_pnl'] as number | null) ?? null,
        // ⚠ `annotated` DEFAULTS TO TRUE IN THE SCHEMA, and absent must not
        // become false here: `annotated === false` is what excludes a trade, so
        // coercing a null to false would silently drop it from every adherence
        // percentage. Null is preserved and the domain treats only an explicit
        // false as unreviewed.
        annotated: (r['annotated'] as boolean | null) ?? null,
        booleans: (r['booleans'] as Record<string, unknown> | null) ?? {},
        mistakeTags: Array.isArray(r['mistake_tags']) ? (r['mistake_tags'] as string[]) : [],
      });
    }

    if (data.length < PAGE_ROWS) break;
  }

  return out;
}
